import React, { useEffect, useRef, useState } from 'react';

/**
 * AnimatedCounter — Counts up from 0 to `value` when scrolled into view
 * 
 * Props:
 * - value: target number
 * - duration: animation duration in ms (default 2000)
 * - prefix: string before number (e.g., '฿')
 * - suffix: string after number (e.g., '+')
 * - separator: use thousands separator (default true)
 * - decimals: decimal places (default 0)
 */
function AnimatedCounter({
    value,
    duration = 2000,
    prefix = '',
    suffix = '',
    separator = true,
    decimals = 0,
    className = '',
    style = {},
}) {
    const ref = useRef(null);
    const [count, setCount] = useState(0);
    const [hasAnimated, setHasAnimated] = useState(false);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && !hasAnimated) {
                    setHasAnimated(true);
                    animateValue(0, value, duration);
                    observer.unobserve(el);
                }
            },
            { threshold: 0.3 }
        );

        observer.observe(el);
        return () => observer.disconnect();
    }, [value, duration, hasAnimated]);

    const animateValue = (start, end, dur) => {
        const startTime = performance.now();

        const easeOutQuart = (t) => 1 - Math.pow(1 - t, 4);

        const animate = (currentTime) => {
            const elapsed = currentTime - startTime;
            const progress = Math.min(elapsed / dur, 1);
            const easedProgress = easeOutQuart(progress);
            const currentValue = start + (end - start) * easedProgress;
            setCount(currentValue);

            if (progress < 1) {
                requestAnimationFrame(animate);
            } else {
                setCount(end);
            }
        };

        requestAnimationFrame(animate);
    };

    const formatNumber = (num) => {
        const fixed = num.toFixed(decimals);
        if (separator) {
            const parts = fixed.split('.');
            parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
            return parts.join('.');
        }
        return fixed;
    };

    return (
        <span ref={ref} className={className} style={style}>
            {prefix}{formatNumber(count)}{suffix}
        </span>
    );
}

export default AnimatedCounter;
